import React, { useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { I18nProvider } from './hooks/useInternationalization';
import { DashboardProvider } from './contexts/DashboardContext';
import { DisclaimerModalProvider } from './contexts/DisclaimerModalContext';
import { queryClient } from './services/queryClient';
import { shouldShowAuth, shouldShowWaitingList, isAuthenticated, enableAuthAccess, getUrlParameter } from './utils/auth';
import { ScrollToTop } from './components/common/navigation';
import { initializeAnimations } from './utils/animationUtils';
import './App.css';

// Lazy load pages for better performance
const LandingPage = React.lazy(() => import('./pages/LandingPage'));
const WaitingListPage = React.lazy(() => import('./pages/WaitingListPage'));
const SignupPage = React.lazy(() => import('./pages/SignupPage'));
const DashboardPage = React.lazy(() => import('./pages/DashboardPage'));
const DemoPage = React.lazy(() => import('./pages/DemoPage'));
const SubjectDetailPage = React.lazy(() => import('./pages/SubjectDetailPage'));
const StudyPlanOverviewPage = React.lazy(() => import('./pages/StudyPlanOverviewPage'));
const LMSManagementPage = React.lazy(() => import('./pages/LMSManagementPage'));
const ExamPaperLibraryPage = React.lazy(() => import('./pages/ExamPaperLibraryPage'));
const ComingSoonPage = React.lazy(() => import('./pages/ComingSoonPage'));
const AccessEnabler = React.lazy(() => import('./components/AccessEnabler'));
const AboutPage = React.lazy(() => import('./pages/AboutPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage'));
const ExtensionPrivacyPage = React.lazy(() => import('./pages/ExtensionPrivacyPage'));
const TermsOfServicePage = React.lazy(() => import('./pages/TermsOfServicePage'));
const PricingPage = React.lazy(() => import('./pages/PricingPage'));
const BillingStatusPage = React.lazy(() => import('./pages/BillingStatusPage'));
const SettingsPage = React.lazy(() => import('./pages/SettingsPage'));
const PlansPage = React.lazy(() => import('./pages/PlansPage'));
const EmailVerificationPage = React.lazy(() => import('./pages/EmailVerificationPage'));

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  return isAuthenticated() ? children : <Navigate to="/signup" replace />;
};

// Auth Route Component (only show if auth is enabled)
const AuthRoute = ({ children }) => {
  return shouldShowAuth() ? children : <Navigate to="/" replace />;
};

function App() {
  useEffect(() => {
    // Initialize animations to prevent flashing
    initializeAnimations();
    
    // Check for auth access parameter in URL
    const authAccess = getUrlParameter('auth_access');
    if (authAccess === 'enabled') {
      enableAuthAccess();
      // Clean up URL parameter
      const url = new URL(window.location);
      url.searchParams.delete('auth_access');
      window.history.replaceState({}, document.title, url.pathname + url.search);
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <I18nProvider>
        <DisclaimerModalProvider>
          <Router>
            <DashboardProvider>
            <ScrollToTop />
            <div className="App">
            <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
              <AnimatePresence mode="wait">
                <Routes>
              {/* Home route */}
              <Route path="/" element={<LandingPage />} />
              
              {/* Access enabler route - for enabling auth via URL parameter */}
              <Route path="/access" element={<AccessEnabler />} />
              
              {/* Company pages - always available */}
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/privacy/extension" element={<ExtensionPrivacyPage />} />
              <Route path="/terms" element={<TermsOfServicePage />} />
              <Route path="/pricing" element={<PricingPage />} />
              
              {/* Email verification page - publicly accessible */}
              <Route path="/verify-email" element={<EmailVerificationPage />} />
              
              {/* Demo pages - always available */}
              <Route path="/demo" element={<DemoPage />} />
              <Route path="/demo/study-plan" element={<StudyPlanOverviewPage isDemo={true} />} />
              <Route path="/demo/subjects/:subjectId" element={<SubjectDetailPage isDemo={true} />} />
              <Route path="/demo/*" element={<ComingSoonPage />} />
              
              {/* Waiting list route - only show if waiting list is enabled and auth is not */}
              {shouldShowWaitingList() && (
                <Route path="/waiting-list" element={<WaitingListPage />} />
              )}
              
              {/* Authentication routes - only show if auth access is enabled */}
              {shouldShowAuth() && (
                <>
                  <Route 
                    path="/login" 
                    element={<Navigate to="/signup" replace />}
                  />
                  <Route 
                    path="/signup" 
                    element={
                      <AuthRoute>
                        <SignupPage />
                      </AuthRoute>
                    } 
                  />
                  <Route 
                    path="/dashboard" 
                    element={
                      <ProtectedRoute>
                        <DashboardPage />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/dashboard/study-plan" 
                    element={
                      <ProtectedRoute>
                        <StudyPlanOverviewPage />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/dashboard/subjects/:subjectId" 
                    element={
                      <ProtectedRoute>
                        <SubjectDetailPage />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/dashboard/exam-papers" 
                    element={
                      <ProtectedRoute>
                        <ExamPaperLibraryPage />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/dashboard/billing/success" 
                    element={
                      <ProtectedRoute>
                        <BillingStatusPage />
                      </ProtectedRoute>
                    } 
                  />
                  <Route 
                    path="/dashboard/settings" 
                    element={
                      <ProtectedRoute>
                        <SettingsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route 
                    path="/dashboard/plans" 
                    element={
                      <ProtectedRoute>
                        <PlansPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route 
                    path="/dashboard/:feature" 
                    element={
                      <ProtectedRoute>
                        <ComingSoonPage />
                      </ProtectedRoute>
                    } 
                  />
                </>
              )}
              
              {/* Redirect authentication routes to home if auth is disabled */}
              {!shouldShowAuth() && (
                <>
                  <Route path="/login" element={<Navigate to="/" replace />} />
                  <Route path="/signup" element={<Navigate to="/" replace />} />
                  <Route path="/dashboard" element={<Navigate to="/" replace />} />
                </>
              )}
              
              {/* Redirect waiting list to auth if auth is enabled */}
              {shouldShowAuth() && !shouldShowWaitingList() && (
                <Route path="/waiting-list" element={<Navigate to="/signup" replace />} />
              )}
              
              {/* Catch all route - redirect to appropriate page */}
              <Route 
                path="*" 
                element={
                  shouldShowAuth() && isAuthenticated() 
                    ? <Navigate to="/dashboard" replace />
                    : <Navigate to="/" replace />
                } 
              />
              </Routes>
            </AnimatePresence>
          </Suspense>
        </div>
            </DashboardProvider>
          </Router>
        </DisclaimerModalProvider>
      </I18nProvider>
    </QueryClientProvider>
  );
}

export default App; 